import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { fieldError, type ParsedApiError } from '@loctopus/shared';
import { ErrorSummary } from '@loctopus/ui';
import { api } from '../../lib/api';
import { toParsedError } from '../../lib/errorDisplay';
import { formatDate } from '../../lib/format';
import type { EventStatus, LoctopusEvent } from '../../lib/types';
import {
  Badge,
  Button,
  DataTable,
  ErrorBanner,
  FormField,
  Input,
  Modal,
  PageHeader,
  Select,
  Spinner,
  Textarea,
} from '../../components/ui';
import { RequirePermission } from '../../lib/auth';

const STATUS_TONE: Record<EventStatus, 'neutral' | 'success' | 'warning' | 'danger' | 'info'> = {
  draft: 'neutral',
  published: 'info',
  on_sale: 'success',
  closed: 'warning',
  cancelled: 'danger',
};

export function EventsPage() {
  const [createOpen, setCreateOpen] = useState(false);
  const navigate = useNavigate();
  const { data, isLoading, error } = useQuery({
    queryKey: ['events'],
    queryFn: () => api.events.list({ limit: 50 }),
  });

  return (
    <div>
      <PageHeader
        title="Events"
        description="Manage events, sessions, ticket types and seat maps."
        actions={
          <RequirePermission permission="events.manage">
            <Button onClick={() => setCreateOpen(true)}>New event</Button>
          </RequirePermission>
        }
      />
      {error && (
        <ErrorBanner message={error instanceof Error ? error.message : 'Failed to load events.'} />
      )}
      {isLoading ? (
        <Spinner />
      ) : (
        <DataTable<LoctopusEvent>
          columns={[
            {
              key: 'title',
              header: 'Event',
              render: (e) => <span className="font-medium text-slate-800">{e.title}</span>,
            },
            { key: 'venue', header: 'Venue', render: (e) => e.venueName ?? '-' },
            {
              key: 'status',
              header: 'Status',
              render: (e) => <Badge tone={STATUS_TONE[e.status]}>{e.status}</Badge>,
            },
            { key: 'mode', header: 'Mode', render: (e) => e.mode },
            { key: 'createdAt', header: 'Created', render: (e) => formatDate(e.createdAt) },
          ]}
          rows={data?.data ?? []}
          getRowKey={(e) => e.id}
          onRowClick={(e) => navigate(`/events/${e.id}`)}
          emptyMessage="No events yet. Create your first event to get started."
        />
      )}
      <CreateEventModal open={createOpen} onClose={() => setCreateOpen(false)} />
    </div>
  );
}

// REAL BUG FOUND AND FIXED: the create-event request never sent `slug` at all (the server's
// createEventSchema - apps/api/src/routes/management/schemas.ts - requires it, no .optional()),
// so every single attempt 400'd with "slug: Required". It also sent `venueName`/`description` as
// `null` when left blank, which that same schema's `.optional()` fields reject just as hard
// ("Expected string, received null") - `.optional()` accepts a missing/undefined key, never an
// explicit null. Fixed below: a slug auto-generated from the title (editable, so the user can
// override it), and omitting blank optional fields entirely instead of sending null.
function slugify(input: string): string {
  const base = input
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
  return base || 'event';
}

function CreateEventModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [timezone, setTimezone] = useState('Australia/Sydney');
  const [venueName, setVenueName] = useState('');
  const [description, setDescription] = useState('');
  const [mode, setMode] = useState<'test' | 'live'>('test');
  const [formError, setFormError] = useState<ParsedApiError | null>(null);

  function handleTitleChange(value: string) {
    setTitle(value);
    // Keep the slug in sync with the title until the user edits the slug field themselves -
    // once they've touched it directly, their choice is never silently overwritten again.
    if (!slugTouched) setSlug(slugify(value));
  }

  const createMutation = useMutation({
    mutationFn: () =>
      api.events.create({
        title,
        slug: slug.trim() || slugify(title),
        timezone,
        ...(venueName.trim() ? { venueName: venueName.trim() } : {}),
        ...(description.trim() ? { description: description.trim() } : {}),
        mode,
        status: 'draft',
      }),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
      onClose();
      navigate(`/events/${created.id}`);
    },
    onError: (err) => setFormError(toParsedError(err)),
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New event"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => createMutation.mutate()}
            disabled={!title.trim() || createMutation.isPending}
          >
            {createMutation.isPending ? 'Creating...' : 'Create event'}
          </Button>
        </>
      }
    >
      {formError && <ErrorSummary error={formError} className="mb-3" />}
      <FormField label="Title">
        <Input
          value={title}
          onChange={(e) => handleTitleChange(e.target.value)}
          placeholder="Winter Comedy Night"
        />
      </FormField>
      <FormField
        label="Slug"
        hint="Used in URLs - auto-generated from the title, edit it if you want something different"
        error={fieldError(formError, 'slug')}
      >
        <Input
          value={slug}
          onChange={(e) => {
            setSlugTouched(true);
            setSlug(e.target.value);
          }}
          placeholder="winter-comedy-night"
        />
      </FormField>
      <FormField label="Venue name" hint="Optional">
        <Input value={venueName} onChange={(e) => setVenueName(e.target.value)} />
      </FormField>
      <FormField label="Timezone" hint="IANA timezone, e.g. Australia/Sydney">
        <Input value={timezone} onChange={(e) => setTimezone(e.target.value)} />
      </FormField>
      <FormField label="Description" hint="Optional">
        <Textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
      </FormField>
      <FormField label="Mode">
        <Select value={mode} onChange={(e) => setMode(e.target.value as 'test' | 'live')}>
          <option value="test">Test</option>
          <option value="live">Live</option>
        </Select>
      </FormField>
    </Modal>
  );
}
